import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { menuOverflowAnimation } from '../../animations';
import { UmssIconComponent } from '../icon/icon.component';

@Component({
  selector: 'umss-menu-overflow',
  standalone: true,
  imports: [CommonModule, UmssIconComponent],
  templateUrl: './menu-overflow.component.html',
  styleUrl: './menu-overflow.component.scss',
  animations: [menuOverflowAnimation]
})
export class UmssMenuOverflowComponent {
  @ViewChild('trigger') triggerElement!: ElementRef;

  constructor(private elementRef: ElementRef) {}

  /** Content props */
  @Input() label: string = 'More options';
  @Input() icon: string = '⋯';
  @Input() ariaLabel?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() variant: 'Default' | 'Compact' = 'Default';
  @Input() direction: 'Up' | 'Down' = 'Down';
  @Input() align: 'Left' | 'Right' = 'Right';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;

  /** Event outputs */
  @Output() overflowClick = new EventEmitter<void>();
  @Output() overflowOpen = new EventEmitter<void>();
  @Output() overflowClose = new EventEmitter<void>();

  /** Internal state */
  private _isOpen: boolean = false;
  menuPosition: { top: string; left: string } = { top: '0', left: '0' };

  get isOpen(): boolean {
    return this._isOpen;
  }

  get menuAnimationParams() {
    const translateY = this.direction === 'Down' ? '-6px' : '6px';
    const originY = this.direction === 'Down' ? 'top' : 'bottom';
    const originX = this.align === 'Right' ? 'right' : 'left';
    return {
      value: true,
      params: { translateY, transformOrigin: `${originY} ${originX}` }
    };
  }

  get displayAriaLabel(): string {
    return this.ariaLabel || this.label;
  }

  /** Event handlers */
  onOverflowToggle(): void {
    if (this.disabled || this.loading) return;

    this._isOpen = !this._isOpen;

    if (this._isOpen) {
      this.calculatePosition();
      this.overflowClick.emit();
      this.overflowOpen.emit();
    } else {
      this.overflowClick.emit();
      this.overflowClose.emit();
    }
  }

  calculatePosition(): void {
    if (!this.triggerElement) return;

    const rect = this.triggerElement.nativeElement.getBoundingClientRect();
    const menuWidth = 160; // Estimated min-width, can be refined if needed or measured after render

    // Default to 'Down' and 'Right' logic for now, tailored to fix the specific issue.
    // In a full implementation, we'd handle all direction/align permutations.

    // Top position
    const top = rect.bottom + 4;
    // Check if it goes off screen bottom would go here if needed

    // Left position (align Right means right edge of menu matches right edge of trigger)
    let left = rect.right - menuWidth;

    if (this.align === 'Left') {
      left = rect.left;
    }

    // Adjust for 'Up' direction if needed
    if (this.direction === 'Up') {
      // We'd need to know menu height, which we don't know before rendering.
      // For now, let's assume standard behavior or rely on CSS for the flip if we weren't using fixed.
      // Since we ARE using fixed, we really should measure.
      // But for this quick fix, let's stick to the requested primary use case: Down/Right
    }

    this.menuPosition = {
      top: `${top}px`,
      left: `${left}px`
    };
  }

  // Close on scroll to prevent detached menu
  @HostListener('window:scroll')
  onWindowScroll() {
    if (this._isOpen) {
      this._isOpen = false;
      this.overflowClose.emit();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this._isOpen) {
      this._isOpen = false;
      this.overflowClose.emit();
    }
  }

  onClickOutside(): void {
    if (this._isOpen) {
      this._isOpen = false;
      this.overflowClose.emit();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.loading) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onOverflowToggle();
        break;
      case 'Escape':
        if (this._isOpen) {
          this._isOpen = false;
          this.overflowClose.emit();
        }
        break;
    }
  }
}
